import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert
} from '@mui/material';
import { useForm } from 'react-hook-form';

export const LisTestItemRefrenceTextRangeForm = ({ 
  onClose, 
  onAdd, 
  parameters = [], 
  existingReferenceRanges = '' // Now expecting string instead of array
}) => {
  const [selectedParameter, setSelectedParameter] = useState('');
  const [referenceRangeText, setReferenceRangeText] = useState('');

  const { handleSubmit, reset } = useForm();

  // Initialize with existing reference range text when parameter is selected
  useEffect(() => {
    if (selectedParameter) {
      const param = parameters.find(p => p._id === selectedParameter || p.tempId === selectedParameter);
      setReferenceRangeText(param?.referenceRangeText || '');
    } else {
      setReferenceRangeText('');
    }
  }, [selectedParameter, parameters]);

  const handleSaveReferenceRange = () => {
    if (!selectedParameter) {
      alert('Please select a parameter first');
      return;
    }

    if (!referenceRangeText.trim()) {
      alert('Please enter reference range text');
      return;
    }

    const referenceRangeData = {
      parameterId: selectedParameter,
      referenceRangeText: referenceRangeText.trim()
    };

    onAdd(referenceRangeData);
    onClose();
  };

  const getParameterName = (parameterId) => {
    const param = parameters.find(p => p._id === parameterId || p.tempId === parameterId);
    return param ? param.name : 'Unknown Parameter';
  };

  const hasExistingReferenceRange = parameters.find(p => 
    (p._id === selectedParameter || p.tempId === selectedParameter)
  )?.referenceRangeText;

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" fontWeight="bold">
          Reference Range Text Management
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Add or edit reference range text for parameters
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Parameter Selection */}
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Select Parameter</InputLabel>
              <Select
                value={selectedParameter}
                onChange={(e) => setSelectedParameter(e.target.value)}
                label="Select Parameter"
              >
                {parameters.map((param) => (
                  <MenuItem key={param._id || param.tempId} value={param._id || param.tempId}>
                    {param.name} {param.unit ? `(${param.unit})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedParameter && (
            <>
              {/* Reference Range Text Form */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Reference Range Text for {getParameterName(selectedParameter)}
                </Typography>
                
                {hasExistingReferenceRange && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    This parameter already has reference range text. Editing will replace the existing one.
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit(handleSaveReferenceRange)}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Reference Range Text
                      </Typography>
                      <TextField
                        multiline
                        rows={6}
                        fullWidth
                        variant="outlined"
                        value={referenceRangeText}
                        onChange={(e) => setReferenceRangeText(e.target.value)}
                        placeholder="Enter reference range text for this parameter (e.g., Normal range: 0-100, Critical values: >200)..."
                        inputProps={{
                          maxLength: 1000
                        }}
                        label="Reference Range Text"
                      />
                      <Typography variant="caption" color="textSecondary">
                        {referenceRangeText.length}/1000 characters
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Existing Reference Range Text Preview */}
              {hasExistingReferenceRange && (
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Current Reference Range Text
                  </Typography>
                  <Box 
                    sx={{ 
                      p: 2,
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      backgroundColor: 'grey.50',
                      maxHeight: '150px',
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {hasExistingReferenceRange}
                  </Box>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSaveReferenceRange} 
          variant="contained" 
          disabled={!selectedParameter || !referenceRangeText.trim()}
        >
          {hasExistingReferenceRange ? 'Update Reference Range' : 'Save Reference Range'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};